import { Menu } from "@/components/sidebar/menu";
import { SidebarToggle } from "@/components/sidebar/sidebar-toggle";
import { Button } from "@/components/ui/button";
import { useSidebar } from "@/hooks/use-sidebar";
import { useStore } from "@/hooks/use-store";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";
import { LogOut } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip";

export function Sidebar() {
  const sidebar = useStore(useSidebar, (x) => x);
  const { logout } = useAuth();
  if (!sidebar) return null;
  const { isOpen, toggleOpen, getOpenState, setIsHover, settings } = sidebar;

  const handleLogout = () => {
    void logout();
  };
  
  return (
    <aside
      className={cn(
        "hidden lg:block lg:fixed top-4 left-4 z-20 h-[calc(100vh-2rem)] translate-x-0 transition-[width] ease-in-out duration-300",
        // Sur mobile: sidebar hidden (sheet-menu used), sur desktop: visible selon getOpenState()
        "lg:w-72",
        !getOpenState() && "lg:w-[90px]",
        settings.disabled && "lg:hidden"
      )}
    >
      <SidebarToggle isOpen={isOpen} setIsOpen={toggleOpen} />
      <div
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        className="relative h-full flex flex-col px-3 py-6 overflow-y-auto rounded-2xl bg-card/50 backdrop-blur-xl border border-border/40 shadow-sm"
      >
        <Button
          className={cn(
            "transition-transform ease-in-out duration-300 mb-6",
            // Sur mobile: toujours translate-x-1, sur desktop: translate-x-0 si ouvert
            "translate-x-1",
            getOpenState() && "lg:translate-x-0"
          )}
          variant="link"
          asChild
        >
          <Link
            to="/portfolio"
            search={{ tab: "dashboard" }}
            className="flex items-center gap-2"
          >
            <img
              src="/wealthscale_light.svg"
              alt="WealthScale Logo"
              width={56}
              height={56}
              className="mr-1 block dark:hidden"
            />
            <img
              src="/wealthscale_dark.svg"
              alt="WealthScale Logo"
              width={56}
              height={56}
              className="mr-1 hidden dark:block"
            />
          </Link>
        </Button>
        {/* Sur mobile: toujours false (via CSS), sur desktop: getOpenState() */}
        <Menu isOpen={getOpenState()} />

        <div className="mt-auto pt-4">
          <TooltipProvider disableHoverableContent>
            <Tooltip delayDuration={100}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  className={cn(
                    "w-full justify-start h-10 text-muted-foreground hover:text-foreground",
                    // Sur mobile: centré, sur desktop: à gauche si ouvert
                    "justify-center",
                    getOpenState() && "lg:justify-start"
                  )}
                  onClick={handleLogout}
                >
                  <span
                    className={cn(
                      isOpen === false ? "lg:mr-0" : "lg:mr-4"
                    )}
                  >
                    <LogOut size={18} />
                  </span>
                  <p
                    className={cn(
                      "max-w-[200px] truncate",
                      // Sur mobile: toujours masqué, sur desktop: selon isOpen
                      "-translate-x-96 opacity-0",
                      isOpen !== false && "lg:translate-x-0 lg:opacity-100",
                      isOpen === false && "lg:-translate-x-96 lg:opacity-0"
                    )}
                  >
                    Logout
                  </p>
                </Button>
              </TooltipTrigger>
              {isOpen === false && (
                <TooltipContent side="right">
                  Logout
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </aside>
  );
}
